<template>
  <div style="position: relative; min-height: 200px">

    <b-loading :active="items === null" :is-full-page="false" />

    <div class="actions">
      <div class="filtering">
        <div class="search">
          <b-field>
            <b-input v-model="query" :placeholder="$t('documents.placeholder')" icon="search" size="is-small" expanded
              :disabled="!items || items.length === 0" />
            <button v-if="query" class="button is-small" @click="query = ''">
              <b-icon icon="times-circle" size="is-small" />
              <span>{{$t('documents.btn-cancel')}}</span>
            </button>
          </b-field>
        </div>
        <div class="unread">
          <input type="checkbox" id="unread-checkbox" v-model="showUnread">
          <span id="unread-label">{{$t('documents.unread-label')}}</span>
        </div>

      </div>

      <div class="expand-all">
        <button class="button is-small" @click.prevent="toggleExpanded">
          <b-icon icon="sitemap" size="is-small" />
          <span>{{ expanded ? $t('documents.close') : $t('documents.open') }}</span>
        </button>
      </div>
    </div>

    <div v-if="query">
      <h1 class="is-size-4">{{$t('documents.query')}}</h1>

      <b-message v-if="filteredItems.length === 0">
        {{$t('documents.queryempty1')}}
      </b-message>
    </div>

    <div v-if="!query && showUnread">
      <b-message v-if="filteredItems.length === 0">
        {{$t('documents.queryempty2')}}
      </b-message>
    </div>

    <b-message v-if="items && items.length === 0">
      {{$t('documents.queryempty3')}}
    </b-message>

    <PortefeuilleArbo v-if="filteredItems.length > 0" ref="arbo" :items="filteredItems" :query="query"
      :showUnreadOnly="showUnread" />

  </div>
</template>

<script>
import _ from 'lodash'
import { mapState } from 'vuex'

import PortefeuilleArbo from '@/components/Portefeuille/Arbo'

import MixinStringHelper from '@/mixins/StringHelper'
import MixinDocumentHelper from '@/mixins/DocumentHelper'

export default {
  mixins: [MixinStringHelper, MixinDocumentHelper],
  components: {
    PortefeuilleArbo,
  },
  data () {
    return {
      query: this.$route.query.search,
      items: null,
      expanded: false,
      showUnread: false
    }
  },
  computed: {
    ...mapState(['documentsRead']),
    filteredItems () {
      if (!this.items || this.items.length === 0) return []

      let tempItems = [...this.items]
      if (this.query || this.isShowUnread()) {
        let reg
        if (!this.query) {
          reg = new RegExp(/.*/, 'i')
        } else {
          const normalizedQuery = this.normalizeString(this.query)
          const escapedQuery = normalizedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
          reg = new RegExp(escapedQuery, 'i')
        }

        tempItems = tempItems.map(item => {
          // Normalize item name before trying to match for reg exp
          if (this.normalizeString(item.name).match(reg)) { return item }

          // Check if level item contains
          // - documents
          // - document collections
          // - documents inside document collections
          // that match the query
          const hasDocumentsMatching = item.documents.find(i => this.normalizeString(i.name).match(reg))
          const hasCollectionsMatching = item.collections.find(i => this.normalizeString(i.name).match(reg))
          const hasDocumentsInCollectionsMatching = item.collections.some(col => col.documents.find(i => this.normalizeString(i.name).match(reg)))
          if (!hasDocumentsMatching && !hasCollectionsMatching && !hasDocumentsInCollectionsMatching) return null

          const itemClone = JSON.parse(JSON.stringify(item))
          itemClone.documents = JSON.parse(JSON.stringify(item.documents))
            .filter(i => this.normalizeString(i.name).match(reg))

          if (hasDocumentsInCollectionsMatching) {
            let tempCollections = []

            item.collections.forEach(col => {
              let colClone = JSON.parse(JSON.stringify(col))
              let matchingDocuments = col.documents.filter(i => this.normalizeString(i.name).match(reg))
              if (matchingDocuments && matchingDocuments.length > 0) {
                colClone.documents = matchingDocuments
                tempCollections.push(colClone)
              }
            })
            itemClone.collections = tempCollections
          } else if (hasCollectionsMatching) {
            itemClone.collections = JSON.parse(JSON.stringify(item.collections))
              .filter(i => this.normalizeString(i.name).match(reg))
          }
          return itemClone
        })
          .filter(i => !!i) // remove null values

        if (this.isShowUnread()) {
          let levelsWithUnreadDocs = []
          // Find levels with unread docs
          tempItems.forEach(item => {
            // If item has documents, it is considered a level-type object
            if (item.documents.length > 0) {
              // Is there even one unread document?
              if (item.documents.some(doc => !this.isRead(doc.id))) {
                // Add to levelsWithUnreadDocs array if it isn't there already
                // id ET niveau : les ids d'arborescence se répètent entre niveaux
                // (types M-Files distincts), l'id seul écartait à tort un dossier
                // homonyme d'un autre niveau.
                if (!levelsWithUnreadDocs.some(elem => elem.id === item.id && elem.level === item.level)) {
                  levelsWithUnreadDocs.push(JSON.parse(JSON.stringify(item)))
                }
              }
            }

            // If item has collections, it is considered a level-type object
            if (item.collections.length > 0) {
              // Check each collection if there's unread documents inside
              item.collections.forEach(col => {
                if (col.documents.some(doc => !this.isRead(doc.id))) {
                  // Add to levelsWithUnreadDocs array if it isn't there already
                  if (!levelsWithUnreadDocs.some(elem => elem['id'] === item.id)) {
                    levelsWithUnreadDocs.push(JSON.parse(JSON.stringify(item)))
                  }
                }
              })
            }
          })

          // When regExp is set to include all files, let's include level items as well
          if (reg.source === '.*') {
            // Find every parent level for levels with unread docs
            levelsWithUnreadDocs.forEach((level) => {
              // Le parent d'un niveau n est au niveau n-1 : la recherche porte sur
              // id ET niveau (les ids se répètent entre niveaux — l'id seul pouvait
              // remonter la mauvaise branche). Le break remplace la boucle sans
              // issue : parent introuvable, le while ne modifiait jamais parent et
              // figeait le navigateur.
              let parent = level.parent
              let parentLevel = level.level - 1
              while (parent !== 0 && parentLevel >= 1) {
                const parentItem = tempItems.find(item => item.id === parent && item.level === parentLevel)
                if (!parentItem) break
                if (!levelsWithUnreadDocs.some(elem => elem.id === parentItem.id && elem.level === parentItem.level)) {
                  // Clone (comme les niveaux poussés plus haut) : les niveaux de
                  // cette liste sont mutés plus bas (retrait des documents lus) —
                  // pousser la référence corrompait this.items, et la liste
                  // complète perdait ses documents lus après un passage du filtre.
                  levelsWithUnreadDocs.push(JSON.parse(JSON.stringify(parentItem)))
                }
                parent = parentItem.parent
                parentLevel--
              }
            })
          }

          // Remove read documents from levels
          levelsWithUnreadDocs.forEach((level) => {
            let newDocs = []
            // Place all unread documents in this level to newDocs
            level.documents.forEach(doc => !this.isRead(doc.id) ? newDocs.push(doc) : _)
            level.documents = newDocs
            let newCols = []
            level.collections.forEach(docCol => {
              // Create document collection from existing collection to hold unread documents
              let tempCol = JSON.parse(JSON.stringify(docCol))
              // Clear documents
              tempCol.documents = []
              docCol.documents.forEach(doc => {
                if (!this.isRead(doc.id)) {
                  // Add document to temporary collection
                  tempCol.documents.push(doc)
                }
              })

              // tempCol (les non-lus) et non docCol (l'original) : une collection
              // entièrement lue restait affichée, vide.
              if (tempCol.documents.length > 0) {
                newCols.push(tempCol)
              }
            })
            level.collections = newCols
          })

          tempItems = levelsWithUnreadDocs.filter(i => !!i)
        }
      }

      // Sort levels
      tempItems.sort((a, b) => a.name.localeCompare(b.name, 'fr', { numeric: true }))
      tempItems.forEach(function (item) {
        // Sort level's documents
        if (item.documents) {
          item.documents.sort((a, b) => a.name.localeCompare(b.name, 'fr', { numeric: true }))
        }
        // Sort level's collections
        if (item.collections) {
          item.collections.sort((a, b) => a.name.localeCompare(b.name, 'fr', { numeric: true }))

          // Sort collection's documents
          item.collections.forEach(col => col.documents.sort((a, b) => a.name.localeCompare(b.name, 'fr', { numeric: true })))
        }
      })
      return tempItems
    }
  },
  watch: {
    '$route.params.bien' () {
      this.getData()
      this.query = ''
    },
    'documentsRead.length' () {
      // Arborescence en cours de chargement (items a null) : la liste lue maintenant est
      // encore vide, et afficherait « Arborescence non disponible. » jusqu'a la reponse
      // (rechargement avec un apercu ouvert, arrivee de l'utilisateur). La fin du chargement
      // tient compte des documents lus.
      if (this.items === null) return
      this.setItemsData()
    },
    expanded () {
      if (this.$refs.arbo) {
        this.$refs.arbo.toggleAll(this.expanded)
      }
    },
    query () {
      if ((this.showUnread || this.query) && !this.expanded) {
        this.expanded = true
      } else if (!this.showUnread && !this.query && this.expanded) {
        this.expanded = false
      }
    },
    showUnread () {
      this.toggleShowUnread()
      if ((this.showUnread || this.query) && !this.expanded) {
        this.expanded = true
      } else if (!this.showUnread && !this.query && this.expanded) {
        this.expanded = false
      }
    }
  },
  created () {
    this.getData()
  },
  methods: {
    async getData () {
      this.expanded = false
      this.showUnread = false
      this.items = null
      try {
        this.$store.commit('inv/ARBO_NOT_RESET')
        await this.$store.dispatch('inv/getArboNot', this.$route.params.bien)
      } catch (e) {
        this.apiError(e)
      }
      this.setItemsData()
    },
    setItemsData () {
      let items = this.$store.state.inv.arboNot

      // Marqueur de passe : la boucle ci-dessous et la recursion de setItemData atteignent
      // les memes items, qui ne doivent etre calcules qu'une fois par passe. L'ensemble est
      // remis a zero a chaque appel, pour que le veilleur sur documentsRead puisse
      // rafraichir les compteurs.
      //
      // La cle joint le niveau a l'identifiant : chaque profondeur est un type d'objet
      // M-Files distinct (117 a 121, 145 a 148), deux niveaux peuvent porter le meme ID.
      const traites = new Set()

      items.forEach((item) => this.setItemData(item, items, traites))
      this.items = items
    },
    setItemData (item, items, traites) {
      const cle = item.level + '/' + item.id
      if (traites.has(cle)) {
        return
      }
      traites.add(cle)

      // modifiedCountOwn conserve la valeur rendue par l'API, qui ne compte que les documents
      // propres a ce niveau et ceux de ses collections (getModifiedCount dans
      // routes/inv_biens.js). modifiedCount porte l'agregat, ajoute plus bas : il est
      // recalcule depuis cette reference a chaque passe, et ne se cumule donc pas.
      if (item.modifiedCountOwn === undefined) {
        this.$set(item, 'modifiedCountOwn', item.modifiedCount || 0)
      }
      this.$set(item, 'modifiedCount', item.modifiedCountOwn)

      // documentsTotal et documentsReadCount ne sont pas portes par la reponse de l'API, ils
      // sont fabriques ici. Vue 2 n'observant que les cles presentes au moment ou il rend un
      // objet reactif, $set est necessaire pour que les pastilles de comptage se peignent.
      const documentIds = item.documents.map(i => i.id)
      this.$set(item, 'documentsReadCount', this.$store.state.documentsRead.filter(i => documentIds.indexOf(i) !== -1).length)
      this.$set(item, 'documentsTotal', item.documents.length)

      // Add collection docs
      item.collections.forEach(col => {
        const colDocumentIds = col.documents.map(i => i.id)
        const colReadCount = this.$store.state.documentsRead.filter(i => colDocumentIds.indexOf(i) !== -1).length

        // Les memes compteurs sont poses sur la collection elle-meme, qu'ArboCollections.vue
        // affiche.
        this.$set(col, 'documentsTotal', col.documents.length)
        this.$set(col, 'documentsReadCount', colReadCount)

        item.documentsTotal += col.documents.length
        item.documentsReadCount += colReadCount
      })

      // subfolders?
      const subfolders = items.filter(j => j.parent === item.id && j.level === item.level + 1)

      // aggregate subfolder's documents count
      subfolders.forEach(subfolder => {
        // Appel inconditionnel : le marqueur de passe, en tete de cette methode, ecarte un
        // sous-dossier deja traite.
        this.setItemData(subfolder, items, traites)

        item.documentsReadCount += subfolder.documentsReadCount
        item.documentsTotal += subfolder.documentsTotal
        item.modifiedCount += subfolder.modifiedCount
      })
    },
    toggleExpanded () {
      if (this.$refs.arbo) {
        this.expanded = !this.expanded
      }
    },
    isShowUnread () {
      return this.$store.state.inv.showUnreadOnly
    },
    toggleShowUnread () {
      this.$store.dispatch('inv/setShowUnreadOnly', this.showUnread)
    }
  },
}
</script>

<style lang="scss" scoped>
h1 {
  margin: 1.5em 0 .5em;
}
</style>
